import { useCallback, useEffect, useState } from 'react';
import {
  FleetOverviewColumnPreferences,
  getDefaultFleetColumnPreferences,
  introduceNewFleetColumns,
  sanitizeFleetColumnPreferences,
} from '@/types/fleetColumnPreferences';

interface UserPreferencesPayload {
  fleetOverviewColumns?: unknown;
}

async function fetchPreferences(): Promise<FleetOverviewColumnPreferences> {
  const res = await fetch('/api/users/me/preferences', { credentials: 'include' });
  if (!res.ok) return getDefaultFleetColumnPreferences();
  const json = await res.json();
  const data = (json.data ?? {}) as UserPreferencesPayload;
  return sanitizeFleetColumnPreferences(data.fleetOverviewColumns) ?? getDefaultFleetColumnPreferences();
}

async function saveFleetColumnPreferences(
  prefs: FleetOverviewColumnPreferences
): Promise<void> {
  const res = await fetch('/api/users/me/preferences', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fleetOverviewColumns: prefs }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || 'Failed to save column preferences');
  }
}

export function useFleetColumnPreferences() {
  const [preferences, setPreferences] = useState<FleetOverviewColumnPreferences>(
    getDefaultFleetColumnPreferences
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await fetchPreferences();
        const { prefs: next, changed } = introduceNewFleetColumns(prefs);
        if (!cancelled) setPreferences(next);
        if (changed && !cancelled) {
          await saveFleetColumnPreferences(next);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (next: FleetOverviewColumnPreferences) => {
    setSaving(true);
    try {
      const merged: FleetOverviewColumnPreferences = {
        order: next.order,
        introduced: next.introduced ?? preferences.introduced,
      };
      await saveFleetColumnPreferences(merged);
      setPreferences(merged);
    } finally {
      setSaving(false);
    }
  }, [preferences.introduced]);

  const resetToDefault = useCallback(async () => {
    const defaults = getDefaultFleetColumnPreferences();
    await save(defaults);
  }, [save]);

  return { preferences, loading, saving, save, resetToDefault };
}
